// LockTooltip — F9.6 WAVE 2G · reusable tooltip for plan-gated nav items.
//
// Shows up on hover of a locked row. Explains WHY the item is locked
// and offers a direct CTA to the pricing page with a tracking fragment
// so the funnel can measure which locked surfaces drive upgrades.
//
// Rendered inline next to the trigger (not via portal) so it inherits
// the dropdown panel's z-index stack. Positioned to the right on
// desktop (fits inside wide panels) and below on mobile/narrow panels.
//
// Props:
//   • item: {key, labelKey, module?} from menuGroups
//   • children: the locked trigger row (span) rendered by the caller;
//               LockTooltip wraps it with hover handlers + absolute panel
//
// Analytics: dispatches a window CustomEvent("glms:lock_cta_click", {detail})
// which existing analytics listeners (if any) can pick up.
import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { Lock } from "lucide-react";

// Minimal mapping from module -> required plan. For now the first paid
// tier (Pro) unlocks most modules; richer tiers can be mapped later.
// Keeping Italian labels inline — tooltip is a small surface and the
// i18n namespace already covers the plan name upstream.
const MODULE_PLAN_MAP = {
    lead_engine:   { tier: "pro",      label: "Pro",      price: "$79/mese" },
    outreach:      { tier: "pro",      label: "Pro",      price: "$79/mese" },
    universe:      { tier: "business", label: "Business", price: "$149/mese" },
    civilization:  { tier: "business", label: "Business", price: "$149/mese" },
    marketplace:   { tier: "pro",      label: "Pro",      price: "$79/mese" },
    creator:       { tier: "pro",      label: "Pro",      price: "$79/mese" },
    affiliate:     { tier: "pro",      label: "Pro",      price: "$79/mese" },
};

const DEFAULT_PLAN = { tier: "pro", label: "Pro", price: "$79/mese" };
const CLOSE_DELAY_MS = 120;

function getPlanForModule(moduleId) {
    return MODULE_PLAN_MAP[moduleId] || DEFAULT_PLAN;
}

export default function LockTooltip({ item, groupKey, children }) {
    const navigate = useNavigate();
    const { t } = useTranslation("header_v3");
    const [open, setOpen] = useState(false);
    const closeTimer = useRef(null);
    const plan = getPlanForModule(item.module);

    const cancelClose = useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);
    const scheduleClose = useCallback(() => {
        cancelClose();
        closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    }, [cancelClose]);

    useEffect(() => () => cancelClose(), [cancelClose]);

    // F9.7 WAVE 2H · fire a lightweight hover event ONCE per tooltip open
    // so analytics can measure intent (not just conversions).
    const hoverFiredRef = useRef(false);
    useEffect(() => {
        if (!open) { hoverFiredRef.current = false; return; }
        if (hoverFiredRef.current) return;
        hoverFiredRef.current = true;
        try {
            window.dispatchEvent(
                new CustomEvent("glms:lock_hover", {
                    detail: {
                        item_id: item.key,
                        from_group: groupKey,
                        module: item.module,
                        required_plan: plan.tier,
                        action: "hover",
                    },
                }),
            );
        } catch (_) {}
    }, [open, item.key, item.module, groupKey, plan.tier]);

    const handleCta = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            try {
                window.dispatchEvent(
                    new CustomEvent("glms:lock_cta_click", {
                        detail: {
                            item_id: item.key,
                            from_group: groupKey,
                            module: item.module,
                            required_plan: plan.tier,
                        },
                    }),
                );
            } catch (_) {}
            const qs = new URLSearchParams({
                from: `lock_${groupKey}_${item.key}`,
                highlight: plan.tier,
            });
            navigate(`/pricing?${qs.toString()}`);
        },
        [item.key, item.module, groupKey, plan.tier, navigate],
    );

    return (
        <div
            className="relative"
            onMouseEnter={() => { cancelClose(); setOpen(true); }}
            onMouseLeave={scheduleClose}
            onFocus={() => { cancelClose(); setOpen(true); }}
            onBlur={scheduleClose}
        >
            {children}

            <AnimatePresence>
                {open && (
                    <motion.div
                        role="tooltip"
                        data-testid={`lock-tooltip-${item.key}`}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -2 }}
                        transition={{ duration: 0.14 }}
                        className="absolute left-full top-0 ml-2 z-[60] w-[260px] rounded-lg border p-3 shadow-2xl"
                        style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
                        onMouseEnter={cancelClose}
                        onMouseLeave={scheduleClose}
                    >
                        <div className="flex items-center gap-2 mb-1.5">
                            <Lock className="w-3.5 h-3.5" style={{ color: "#FFFFFF" }} aria-hidden />
                            <span className="text-sm font-semibold text-white">
                                {t(item.labelKey)}
                            </span>
                        </div>
                        <p className="text-xs text-[#CCCCCC] mb-3 leading-relaxed">
                            Disponibile con <span className="text-white font-medium">{plan.label}</span>
                            {" — "}
                            <span className="tabular-nums">{plan.price}</span>
                        </p>
                        <button
                            type="button"
                            onClick={handleCta}
                            data-testid={`lock-cta-${item.key}`}
                            className="w-full inline-flex items-center justify-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-white transition-colors"
                            style={{ backgroundColor: "#FFFFFF" }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#EDEDED"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#FFFFFF"; }}
                        >
                            Sblocca {plan.label} →
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
